import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { ReactComponent as SpeakerOffIcon } from 'assets/icons/speaker_off.svg'
import { ReactComponent as SpeakerOnIcon } from 'assets/icons/speaker_on.svg'
import styles from './musicToggleButton.module.scss'

interface MusicToggleButtonProps {
  className?: string
}

// YouTube 영상 ID: GxldQ9eX2wo (Until I Found You)
const YOUTUBE_VIDEO_ID = 'GxldQ9eX2wo'

const MusicToggleButton = ({ className }: MusicToggleButtonProps) => {
  const playerRef = useRef<YT.Player | null>(null)
  const playerContainerRef = useRef<HTMLDivElement | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isWarningVisible, setIsWarningVisible] = useState(false)

  useEffect(() => {
    // YouTube IFrame API가 로드될 때까지 대기
    const initializeYouTubePlayer = () => {
      if (!window.YT || !window.YT.Player) {
        // YouTube API가 아직 로드되지 않았으면 재시도
        setTimeout(initializeYouTubePlayer, 100)
        return
      }

      // 플레이어 컨테이너 생성 (화면 밖에 숨김)
      const container = document.createElement('div')
      container.id = 'youtube-player-container'
      container.style.position = 'fixed'
      container.style.top = '-9999px'
      container.style.left = '-9999px'
      container.style.width = '1px'
      container.style.height = '1px'
      container.style.opacity = '0'
      container.style.pointerEvents = 'none'
      document.body.appendChild(container)
      playerContainerRef.current = container

      // YouTube 플레이어 초기화
      const player = new window.YT.Player(container.id, {
        videoId: YOUTUBE_VIDEO_ID,
        width: 1,
        height: 1,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          enablejsapi: 1,
          fs: 0,
          iv_load_policy: 3,
          loop: 1, // 반복 재생
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          showinfo: 0,
        },
        events: {
          onReady: (event) => {
            setIsReady(true)
            // 초기 볼륨 설정 (선택사항)
            event.target.setVolume(70)
          },
          onStateChange: (event) => {
            // 플레이어 상태 변경 감지
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true)
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false)
            } else if (event.data === window.YT.PlayerState.ENDED) {
              // 반복 재생을 위해 다시 재생
              event.target.playVideo()
            }
          },
          onError: () => {
            setIsReady(false)
            setIsPlaying(false)
          },
        },
      })

      playerRef.current = player
    }

    // YouTube API 로드 확인
    if (window.YT && window.YT.Player) {
      initializeYouTubePlayer()
    } else {
      // onYouTubeIframeAPIReady 콜백 설정
      window.onYouTubeIframeAPIReady = initializeYouTubePlayer
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch (e) {
          // 무시
        }
        playerRef.current = null
      }
      if (playerContainerRef.current) {
        playerContainerRef.current.remove()
        playerContainerRef.current = null
      }
      setIsReady(false)
      setIsPlaying(false)
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
      setIsWarningVisible(false)
    }
  }, [])

  const showPreparingAlert = () => {
    setIsWarningVisible(true)
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }
    warningTimeoutRef.current = setTimeout(() => {
      setIsWarningVisible(false)
      warningTimeoutRef.current = null
    }, 3000)
  }

  const toggleMusic = () => {
    if (!playerRef.current || !isReady) {
      showPreparingAlert()
      return
    }

    try {
      if (isPlaying) {
        playerRef.current.pauseVideo()
        playerRef.current.seekTo(0, true)
        setIsPlaying(false)
      } else {
        playerRef.current.playVideo()
        setIsPlaying(true)
      }
    } catch (error) {
      console.error('YouTube player error:', error)
      setIsReady(false)
      setIsPlaying(false)
    }
  }

  return (
    <div className={classNames(styles.wrapper, className)}>
      <button
        type='button'
        className={styles.musicToggle}
        aria-label={isPlaying ? '배경 음악 정지' : '배경 음악 재생'}
        aria-pressed={isPlaying}
        aria-busy={!isReady}
        aria-disabled={!isReady}
        onClick={toggleMusic}
      >
        {!isReady && <span className={styles.loader} aria-hidden='true' />}
        {isReady && (isPlaying ? <SpeakerOnIcon /> : <SpeakerOffIcon />)}
      </button>
      {isWarningVisible && (
        <div className={styles.alert} role='status' aria-live='polite'>
          BGM을 준비중입니다. 잠시만 기다려주세요.
        </div>
      )}
    </div>
  )
}

export default MusicToggleButton
